import React, { useState } from 'react';
import ToolLayout from '../components/ToolLayout';
import { validateAndFixJson } from '../utils/jsonUtils';
// @ts-ignore
import ReactJson from 'react-json-view';
import * as XLSX from 'xlsx';

const JsonFormatter: React.FC = () => {
  const [input, setInput] = useState('');
  const [output, setOutput] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [wasFixed, setWasFixed] = useState(false);
  const [uploadedFileName, setUploadedFileName] = useState('');
  const [jsonData, setJsonData] = useState<any>(null);
  const [viewMode, setViewMode] = useState<'formatted' | 'interactive'>('interactive');
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportableObjects, setExportableObjects] = useState<Array<{path: string, data: any, type: 'object' | 'array', description: string}>>([]);

  const formatJson = () => {
    setError('');
    setSuccess('');
    setWasFixed(false);
    
    const result = validateAndFixJson(input);
    
    if (result.success && result.data) {
      const formatted = JSON.stringify(result.data, null, 2);
      setOutput(formatted);
      setJsonData(result.data);
      
      if (result.wasFixed) {
        setSuccess('JSON đã được sửa và format thành công! 🔧✨');
        setWasFixed(true);
      } else {
        setSuccess('JSON đã được format thành công! ✅');
        setWasFixed(false);
      }
    } else {
      setError(result.error || 'Có lỗi xảy ra');
      setOutput('');
      setJsonData(null);
    }
  };

  const minifyJson = () => {
    setError('');
    setSuccess('');
    setWasFixed(false);
    
    const result = validateAndFixJson(input);
    
    if (result.success && result.data) {
      const minified = JSON.stringify(result.data);
      setOutput(minified);
      setJsonData(result.data);
      
      if (result.wasFixed) {
        setSuccess('JSON đã được sửa và minify thành công! 🔧✨');
        setWasFixed(true);
      } else {
        setSuccess('JSON đã được minify thành công! ✅');
        setWasFixed(false);
      }
    } else {
      setError(result.error || 'Có lỗi xảy ra');
      setOutput('');
      setJsonData(null);
    }
  };

  const validateJson = () => {
    setError('');
    setSuccess('');
    setWasFixed(false);
    
    const result = validateAndFixJson(input);
    
    if (result.success) {
      if (result.wasFixed) {
        setSuccess('JSON có thể được sửa và là hợp lệ! 🔧✓');
        setWasFixed(true);
      } else {
        setSuccess('JSON hợp lệ! ✅');
        setWasFixed(false);
      }
      setOutput('');
      setJsonData(null);
    } else {
      setError(result.error || 'JSON không hợp lệ');
      setOutput('');
      setJsonData(null);
    }
  };

  const smartFixJson = () => {
    setError('');
    setSuccess('');
    setWasFixed(false);
    
    const result = validateAndFixJson(input);
    
    if (result.success && result.fixedInput && result.wasFixed) {
      // Show the fixed input in the input field
      setInput(result.fixedInput);
      setSuccess('JSON đã được sửa tự động! Bạn có thể xem thay đổi ở input. 🔧✨');
      setWasFixed(true);
      setOutput('');
      setJsonData(null);
    } else if (result.success && !result.wasFixed) {
      setSuccess('JSON đã đúng chuẩn rồi! ✅');
      setOutput('');
      setJsonData(null);
    } else {
      setError(result.error || 'Không thể sửa JSON này');
      setOutput('');
      setJsonData(null);
    }
  };

  const clearAll = () => {
    setInput('');
    setOutput('');
    setError('');
    setSuccess('');
    setWasFixed(false);
    setUploadedFileName('');
    setJsonData(null);
  };

  const copyToClipboard = async () => {
    if (output) {
      try {
        await navigator.clipboard.writeText(output);
        setSuccess('Đã copy vào clipboard!');
      } catch (err) {
        setError('Không thể copy vào clipboard');
      }
    }
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) {return;}

    // Check file type
    const validTypes = ['application/json', 'text/plain', 'text/json'];
    const isValidType = validTypes.includes(file.type) || file.name.endsWith('.json');
    
    if (!isValidType) {
      setError('Vui lòng chọn file JSON hợp lệ (.json)');
      setSuccess('');
      return;
    }

    // Check file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      setError('File quá lớn! Vui lòng chọn file nhỏ hơn 10MB.');
      setSuccess('');
      return;
    }

    const reader = new FileReader();
    
    reader.onload = (e) => {
      try {
        const content = e.target?.result as string;
        setInput(content);
        setUploadedFileName(file.name);
        setSuccess(`Đã tải file "${file.name}" thành công! 📁`);
        setError('');
        setOutput('');
        setJsonData(null);
      } catch (err) {
        setError('Không thể đọc file: ' + (err as Error).message);
        setSuccess('');
      }
    };

    reader.onerror = () => {
      setError('Lỗi khi đọc file. Vui lòng thử lại.');
      setSuccess('');
    };

    reader.readAsText(file, 'UTF-8');
  };

  const downloadJson = () => {
    if (!output) {
      setError('Không có dữ liệu để download');
      return;
    }

    try {
      const blob = new Blob([output], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      // Generate filename
      const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, '-');
      const filename = uploadedFileName 
        ? uploadedFileName.replace('.json', '_formatted.json')
        : `formatted_json_${timestamp}.json`;
      
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      setSuccess('File JSON đã được download! 💾');
    } catch (err) {
      setError('Không thể download file: ' + (err as Error).message);
    }
  };

  // Analyze JSON structure to find exportable objects/arrays
  const analyzeJsonStructure = (data: any, path: string = 'root'): Array<{path: string, data: any, type: 'object' | 'array', description: string}> => {
    const exportable: Array<{path: string, data: any, type: 'object' | 'array', description: string}> = [];
    
    const traverse = (obj: any, currentPath: string) => {
      if (Array.isArray(obj)) {
        if (obj.length > 0) {
          const description = `Array với ${obj.length} items${obj[0] && typeof obj[0] === 'object' ? ` (${Object.keys(obj[0]).length} columns)` : ''}`;
          exportable.push({
            path: currentPath,
            data: obj,
            type: 'array',
            description
          });
        }
        
        // Continue traversing array items
        obj.forEach((item, index) => {
          if (typeof item === 'object' && item !== null) {
            traverse(item, `${currentPath}[${index}]`);
          }
        });
      } else if (typeof obj === 'object' && obj !== null) {
        const keys = Object.keys(obj);
        if (keys.length > 0) {
          const description = `Object với ${keys.length} properties`;
          exportable.push({
            path: currentPath,
            data: obj,
            type: 'object',
            description
          });
        }
        
        // Continue traversing object properties
        Object.entries(obj).forEach(([key, value]) => {
          if (typeof value === 'object' && value !== null) {
            traverse(value, `${currentPath}.${key}`);
          }
        });
      }
    };
    
    traverse(data, path);
    return exportable;
  };

  // Export to Excel
  const exportToExcel = (selectedData: any, path: string) => {
    try {
      let worksheetData: any[] = [];
      let sheetName = 'Sheet1';
      
      if (Array.isArray(selectedData)) {
        if (selectedData.length === 0) {
          setError('Array rỗng, không thể export');
          return;
        }
        
        // Get headers from first object
        const firstItem = selectedData[0];
        if (typeof firstItem === 'object' && firstItem !== null) {
          // Extract headers from first object
          const headers = Object.keys(firstItem);
          
          // Convert array of objects to array of arrays for Excel
          worksheetData = selectedData.map(item => {
            const row: any[] = [];
            headers.forEach(header => {
              const value = item[header];
              // Handle nested objects/arrays
              if (typeof value === 'object' && value !== null) {
                row.push(JSON.stringify(value));
              } else {
                row.push(value);
              }
            });
            return row;
          });
          
          // Add headers as first row
          worksheetData.unshift(headers);
        } else {
          // Array of primitives
          worksheetData = selectedData.map((item, index) => [index, item]);
          worksheetData.unshift(['Index', 'Value']);
        }
        
        sheetName = path.replace(/\./g, '_').replace(/\[|\]/g, '') || 'Array_Data';
      } else if (typeof selectedData === 'object' && selectedData !== null) {
        // Single object - convert to key-value pairs
        worksheetData = Object.entries(selectedData).map(([key, value]) => {
          if (typeof value === 'object' && value !== null) {
            return [key, JSON.stringify(value)];
          }
          return [key, value];
        });
        worksheetData.unshift(['Property', 'Value']);
        sheetName = path.replace(/\./g, '_') || 'Object_Data';
      }
      
      // Create workbook and worksheet
      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.aoa_to_sheet(worksheetData);
      
      // Auto-size columns
      const colWidths = worksheetData[0]?.map((_item: any, colIndex: number) => {
        const maxLength = Math.max(
          ...worksheetData.map(row => 
            row[colIndex] ? String(row[colIndex]).length : 0
          )
        );
        return { width: Math.min(Math.max(maxLength + 2, 10), 50) };
      });
      
      if (colWidths) {
        ws['!cols'] = colWidths;
      }
      
      XLSX.utils.book_append_sheet(wb, ws, sheetName);
      
      // Generate filename
      const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, '-');
      const baseFilename = uploadedFileName 
        ? uploadedFileName.replace('.json', '')
        : 'json_export';
      const filename = `${baseFilename}_${sheetName}_${timestamp}.xlsx`;
      
      // Download file
      XLSX.writeFile(wb, filename);
      
      setSuccess(`✅ Exported to Excel: ${filename}`);
      setShowExportModal(false);
    } catch (err) {
      setError('Không thể export Excel: ' + (err as Error).message);
    }
  };

  // Show export modal
  const showExportOptions = () => {
    if (!jsonData) {
      setError('Không có dữ liệu JSON để export');
      return;
    }
    
    const exportableData = analyzeJsonStructure(jsonData);
    setExportableObjects(exportableData);
    setShowExportModal(true);
  };

  return (
    <ToolLayout 
      title="JSON Formatter" 
      description="Format, validate, và beautify JSON data một cách dễ dàng"
    >
      {/* File Upload Section */}
      <div className="input-group">
        <label className="input-label">📁 Upload JSON File</label>
        <div className="file-upload-container">
          <input
            type="file"
            id="json-file-upload"
            accept=".json,application/json,text/plain"
            onChange={handleFileUpload}
            style={{ display: 'none' }}
          />
          <label htmlFor="json-file-upload" className="file-upload-button">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
              <polyline points="17 8 12 3 7 8"/>
              <line x1="12" y1="3" x2="12" y2="15"/>
            </svg>
            Choose File
          </label>
          {uploadedFileName && (
            <div className="uploaded-file-badge">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M13 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z"/>
                <polyline points="13 2 13 9 20 9"/>
              </svg>
              {uploadedFileName}
            </div>
          )}
        </div>
      </div>

      <style>{`
        .file-upload-container {
          display: flex;
          align-items: center;
          gap: 1rem;
          flex-wrap: wrap;
        }

        .file-upload-button {
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.75rem 1.25rem;
          background: var(--accent-primary);
          color: white;
          border: none;
          border-radius: 10px;
          font-size: 0.9375rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
          user-select: none;
        }

        .file-upload-button:hover {
          background: var(--accent-hover);
          transform: translateY(-2px);
          box-shadow: var(--shadow-md);
        }

        .file-upload-button:active {
          transform: translateY(0);
        }

        .uploaded-file-badge {
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.5rem 1rem;
          background: var(--accent-light);
          color: var(--accent-primary);
          border-radius: 8px;
          font-size: 0.875rem;
          font-weight: 500;
          border: 1px solid var(--accent-primary);
        }

        .uploaded-file-badge svg {
          flex-shrink: 0;
        }

        /* Modal Styles */
        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background-color: rgba(0, 0, 0, 0.6);
          display: flex;
          justify-content: center;
          align-items: center;
          z-index: 1000;
          backdrop-filter: blur(4px);
          animation: fadeIn 0.2s ease;
        }

        .modal-content {
          background: var(--card-bg);
          border-radius: 16px;
          padding: 2rem;
          max-width: 600px;
          width: 90%;
          max-height: 70vh;
          overflow-y: auto;
          box-shadow: var(--shadow-lg);
          border: 1px solid var(--border-color);
          animation: slideUp 0.3s ease;
        }

        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }

        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .modal-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 1.5rem;
        }

        .modal-title {
          margin: 0;
          color: var(--text-primary);
          font-size: 1.5rem;
          font-weight: 700;
        }

        .modal-close {
          background: none;
          border: none;
          padding: 0.5rem;
          cursor: pointer;
          color: var(--text-secondary);
          border-radius: 8px;
          transition: all 0.2s ease;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .modal-close:hover {
          background: var(--bg-tertiary);
          color: var(--text-primary);
        }

        .modal-description {
          color: var(--text-secondary);
          margin-bottom: 1.5rem;
          font-size: 0.9375rem;
          line-height: 1.6;
        }

        .modal-empty {
          text-align: center;
          padding: 2rem;
          color: var(--text-secondary);
        }

        .modal-section-header h4 {
          margin: 0 0 1rem 0;
          color: var(--text-primary);
          font-size: 1rem;
          font-weight: 600;
        }

        .export-items-list {
          max-height: 300px;
          overflow-y: auto;
          margin-bottom: 1rem;
        }

        .export-item {
          border: 2px solid var(--border-color);
          border-radius: 10px;
          padding: 1rem;
          margin-bottom: 1rem;
          cursor: pointer;
          transition: all 0.2s ease;
          background: var(--bg-secondary);
        }

        .export-item:hover {
          border-color: var(--accent-primary);
          background: var(--accent-light);
          transform: translateY(-2px);
        }

        .export-item-content {
          display: flex;
          justify-content: space-between;
          align-items: center;
          gap: 1rem;
        }

        .export-item-title {
          font-weight: 600;
          color: var(--text-primary);
          margin-bottom: 0.25rem;
        }

        .export-item-desc {
          font-size: 0.875rem;
          color: var(--text-secondary);
        }

        .export-item-badge {
          color: white;
          padding: 0.25rem 0.75rem;
          border-radius: 12px;
          font-size: 0.75rem;
          font-weight: 600;
          white-space: nowrap;
        }

        .export-item-badge.array {
          background: var(--success);
        }

        .export-item-badge.object {
          background: var(--accent-primary);
        }

        .modal-tip {
          margin-top: 1.5rem;
          padding-top: 1rem;
          border-top: 1px solid var(--border-color);
          font-size: 0.8125rem;
          color: var(--text-secondary);
          line-height: 1.6;
        }

        .modal-tip strong {
          color: var(--text-primary);
        }
      `}</style>

      <div style={{ 
        textAlign: 'center', 
        margin: '1rem 0',
        color: '#6b7280',
        fontSize: '0.9rem',
        fontWeight: '500'
      }}>
        ━━━━━ hoặc ━━━━━
      </div>

      <div className="input-group">
        <label className="input-label">✏️ Nhập JSON thủ công:</label>
        <textarea
          className="input-textarea"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder={'Nhập JSON của bạn ở đây... (hỗ trợ JSON không chuẩn)\n\nVí dụ - tất cả sẽ được xử lý:\n{"name": "John", "age": 30,} ← có trailing comma\n{name: "John", age: 30} ← không có quotes\n{\'name\': \'John\'} ← single quotes'}
        />
      </div>

      <div className="button-group">
        <button className="action-button" onClick={formatJson}>
          🎨 Smart Format
        </button>
        <button className="action-button" onClick={minifyJson}>
          📦 Smart Minify
        </button>
        <button className="action-button" onClick={validateJson}>
          ✅ Validate
        </button>
        <button className="action-button" onClick={smartFixJson}>
          🔧 Auto Fix
        </button>
        <button className="action-button secondary" onClick={clearAll}>
          Clear All
        </button>
      </div>

      {error && <div className="error-message">{error}</div>}
      {success && <div className="success-message">{success}</div>}

      {(output || jsonData) && (
        <div className="input-group">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
            <label className="input-label">Kết quả:</label>
            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
              {/* View Mode Toggle */}
              <div style={{ display: 'flex', gap: '0.25rem', marginRight: '1rem' }}>
                <button 
                  className={`action-button ${viewMode === 'interactive' ? '' : 'secondary'}`}
                  onClick={() => setViewMode('interactive')}
                  style={{ fontSize: '0.8rem', padding: '0.5rem 0.75rem' }}
                >
                  🎯 Interactive
                </button>
                <button 
                  className={`action-button ${viewMode === 'formatted' ? '' : 'secondary'}`}
                  onClick={() => setViewMode('formatted')}
                  style={{ fontSize: '0.8rem', padding: '0.5rem 0.75rem' }}
                >
                  📝 Text
                </button>
              </div>
              
              <button className="action-button" onClick={copyToClipboard}>
                📋 Copy
              </button>
              <button className="action-button" onClick={downloadJson}>
                💾 Download
              </button>
              <button className="action-button" onClick={showExportOptions}>
                📊 Export Excel
              </button>
            </div>
          </div>

          {viewMode === 'interactive' && jsonData ? (
            <div 
              style={{
                border: '2px solid #e5e7eb',
                borderRadius: '8px',
                padding: '0',
                backgroundColor: '#fafafa',
                maxHeight: '500px',
                overflowY: 'auto',
                textAlign: 'left',
                width: '100%'
              }}
            >
              <style>{`
                .react-json-view {
                  background-color: #fafafa !important;
                  padding: 1rem !important;
                }
                .react-json-view .object-key-val {
                  padding-left: 0 !important;
                }
                .react-json-view .object-content {
                  margin-left: 0 !important;
                  padding-left: 1rem !important;
                }
                .react-json-view .array-content {
                  margin-left: 0 !important;
                  padding-left: 1rem !important;
                }
                .react-json-view .object-key-val > .object-key {
                  color: #1e40af !important;
                  font-weight: 600 !important;
                }
                .react-json-view .string-value {
                  color: #059669 !important;
                }
                .react-json-view .number-value {
                  color: #dc2626 !important;
                }
                .react-json-view .boolean-value {
                  color: #7c3aed !important;
                }
                .react-json-view .null-value {
                  color: #6b7280 !important;
                }
              `}</style>
              <ReactJson
                src={jsonData}
                theme="rjv-default"
                name={false}
                displayDataTypes={false}
                displayObjectSize={false}
                enableClipboard={true}
                collapsed={2}
                indentWidth={2}
                iconStyle="triangle"
                shouldCollapse={(field: any) => {
                  // Auto-collapse arrays/objects với nhiều hơn 8 items để tránh quá tải
                  if (field.type === 'array' && Array.isArray(field.src) && field.src.length > 8) {return true;}
                  if (field.type === 'object' && field.src && typeof field.src === 'object' && Object.keys(field.src).length > 8) {return true;}
                  return false;
                }}
                style={{
                  fontSize: '14px',
                  fontFamily: 'Monaco, Menlo, Ubuntu Mono, monospace',
                  backgroundColor: 'transparent',
                  width: '100%',
                  lineHeight: '1.5'
                }}
              />
            </div>
          ) : (
            <textarea
              className="input-textarea"
              value={output}
              readOnly
              style={{ fontFamily: 'Monaco, Menlo, Ubuntu Mono, monospace' }}
            />
          )}
        </div>
      )}

      {/* Excel Export Modal */}
      {showExportModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3 className="modal-title">📊 Export to Excel</h3>
              <button className="modal-close" onClick={() => setShowExportModal(false)}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="18" y1="6" x2="6" y2="18"/>
                  <line x1="6" y1="6" x2="18" y2="18"/>
                </svg>
              </button>
            </div>

            <p className="modal-description">
              Chọn object hoặc array bạn muốn export. Arrays sẽ được export thành table với headers từ object đầu tiên.
            </p>

            {exportableObjects.length === 0 ? (
              <div className="modal-empty">
                <p>Không tìm thấy object hoặc array có thể export trong JSON này.</p>
                <button
                  className="action-button secondary"
                  onClick={() => setShowExportModal(false)}
                >
                  Đóng
                </button>
              </div>
            ) : (
              <div>
                <div className="modal-section-header">
                  <h4>Tìm thấy {exportableObjects.length} object/array có thể export:</h4>
                </div>

                <div className="export-items-list">
                  {exportableObjects.map((item, index) => (
                    <div
                      key={index}
                      className="export-item"
                      onClick={() => exportToExcel(item.data, item.path)}
                    >
                      <div className="export-item-content">
                        <div>
                          <div className="export-item-title">
                            {item.type === 'array' ? '📋' : '📄'} {item.path}
                          </div>
                          <div className="export-item-desc">
                            {item.description}
                          </div>
                        </div>
                        <div className={`export-item-badge ${item.type === 'array' ? 'array' : 'object'}`}>
                          {item.type.toUpperCase()}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="modal-tip">
                  💡 <strong>Tip:</strong> Arrays sẽ được export thành bảng với headers từ object đầu tiên.
                  Objects sẽ được export dạng key-value pairs.
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </ToolLayout>
  );
};

export default JsonFormatter;
